import { Alert } from '@apollo/orbit';
import classnames from 'classnames';
import * as monacoEditor from 'monaco-editor';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { Link } from 'react-router-dom';

import {
  API_SCHEMA_ID,
  SUPERGRAPH_SCHEMA_ID,
} from 'src/app/graph/documentationPage/sdlPage/sdlDisplay/SDLDisplay';
import { GraphRef } from 'src/app/graph/hooks/useGraphRef';
import { proposalEditorRouteConfig } from 'src/app/graph/routes';
import { KeyboardShortcutModal } from 'src/components/common/keyboardShortcutModal/KeyboardShortcutModal';
import { LazyUrlModal } from 'src/components/common/LazyUrlModal';
import { PermissionGuard } from 'src/components/permissionGuards/permissionGuard/PermissionGuard';
import {
  hotkeys,
  proposalEditorKeyboardShortcuts,
  useKeypressListener,
} from 'src/hooks/useKeypressListener/useKeypressListener';
import { useRouteParams } from 'src/hooks/useRouteParams';
import Config from 'src/lib/config';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { isMinLengthArray } from 'src/lib/isMinLengthArray';
import { useLDFlag } from 'src/lib/launchDarkly';
import { RouteConfig } from 'src/lib/routeConfig/RouteConfig';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { SDLShimmerLoading } from '../../../../../components/common/loading/SDLShimmerLoading';
import { DeleteCommentModal } from '../../proposalCommentDrawer/deleteCommentModal/DeleteCommentModal';
import { ProposalCommentDrawer } from '../../proposalCommentDrawer/ProposalCommentDrawer';
import { ProposalSchemaInfo } from '../hooks/useProposalEditorState/useProposalEditorState';
import { DrawerVisibilityState } from '../shared';

import { ProposalEditorActions } from './proposalEditorActions/ProposalEditorActions';
import { ProposalSDLEditor } from './proposalSDLEditor/ProposalSDLEditor';
import {
  SchemaLinterDrawer,
  SchemaLinterDrawerLinterRuleLevelConfigurationFragment,
} from './schemaLinterDrawer/SchemaLinterDrawer';
import { UnattributedDiagnosticsDrawer } from './unattributedDiagnosticsDrawer/UnattributedDiagnosticsDrawer';

export { SchemaLinterDrawerLinterRuleLevelConfigurationFragment };
export const ProposalSDLDisplay = ({
  className,
  graphRef,
  selectedSchemaInfo,
  editor,
  buildErrors,
  isStale,
  onSaveRevision,
  collapsed,
  currentVisibleDrawer,
  setCurrentVisibleDrawer,
  subgraphsHaveChanges,
  sdlIsLoading,
  dataIsNotConsistent,
  changedSubgraphs,
  linterRules,
  saveRevisionDisabled,
  proposalId,
  parentComments,
  implementedChanges,
  canQueryLaunches,
  sourceGraphVariant,
  selectedSubgraphHasChanges,
  proposedSubgraphs,
  proposedSupergraphSchema,
}: {
  className?: string;
  graphRef: GraphRef;
  selectedSchemaInfo: ProposalSchemaInfo;
  editor: monacoEditor.editor.IStandaloneCodeEditor;
  buildErrors: GraphQLTypes.BuildError[] | undefined;
  isStale: boolean;
  onSaveRevision: () => void;
  collapsed: boolean;
  currentVisibleDrawer: DrawerVisibilityState;
  setCurrentVisibleDrawer: React.Dispatch<
    React.SetStateAction<DrawerVisibilityState>
  >;
  subgraphsHaveChanges: boolean;
  hasSubgraphs: boolean;
  sdlIsLoading: boolean;
  dataIsNotConsistent: boolean | undefined;
  changedSubgraphs: Record<string, { changed: boolean; isEmpty: boolean }>;
  linterRules?: GraphQLTypes.ProposalEditorQuery_service_linterConfiguration_rules[];
  saveRevisionDisabled: boolean;
  canEditProposal: boolean;
  proposalId: string;
  parentComments: GraphQLTypes.ProposalEditorQuery_service_variant_proposal_parentComments_ParentChangeProposalComment[];
  implementedChanges: Omit<
    GraphQLTypes.ImplementedChangesFragment['implementedChanges'][0],
    'subgraph'
  >[];
  canQueryLaunches: boolean;
  sourceGraphVariant: string | undefined;
  selectedSubgraphHasChanges: boolean;
  proposedSubgraphs: Array<{ name: string; hash: string | undefined }> | null;
  proposedSupergraphSchema: {
    hash: string | null;
    getSdl?: () => string | undefined;
  };
}) => {
  const history = useHistory();
  const location = useLocation();
  const shortcuts = proposalEditorKeyboardShortcuts;
  const {
    linkedCoordinateName: selectedCoordinate,
    linkedCommentId: selectedCommentId,
  } = useRouteParams(proposalEditorRouteConfig);

  const shouldShowLinter = useLDFlag('nebula-proposals-linter');

  const onCloseCommentDrawer = useCallback(() => {
    setCurrentVisibleDrawer('none');
    history.push(
      proposalEditorRouteConfig.locationFrom({
        location,
        patch: { linkedCoordinateName: RouteConfig.DELETE },
        fromRouteConfig: proposalEditorRouteConfig,
      }),
    );
  }, [history, location, setCurrentVisibleDrawer]);

  const [keyboardModalVisible, setKeyboardModalVisible] = useState(false);
  useKeypressListener(hotkeys.toggleShortcutModal, () => {
    if (selectedSchemaInfo.type === 'Subgraph') {
      setKeyboardModalVisible((value) => !value);
    }
  });

  useKeypressListener(hotkeys.saveRevision, () => {
    if (
      subgraphsHaveChanges &&
      !Object.values(changedSubgraphs).some((subgraph) => subgraph.isEmpty) &&
      !isStale &&
      !saveRevisionDisabled
    ) {
      onSaveRevision();
      history.push(
        setOverlayVisibleLocation({
          location,
          overlayName: Config.modals.publishRevision,
        }),
      );
    }
  });

  // Note: When we open a coordinate we should open the comment section and close
  // the current visible section if any
  useEffect(() => {
    if (selectedCoordinate) {
      setCurrentVisibleDrawer('comment');
    }
  }, [selectedCoordinate, setCurrentVisibleDrawer]);
  // Note: If we switch schemas we should hide the drawer if it is open
  useEffect(() => {
    return () => {
      setCurrentVisibleDrawer('none');
    };
  }, [selectedSchemaInfo.displayName, setCurrentVisibleDrawer]);

  const commentThreadToOpen = useMemo(
    () =>
      parentComments.find(
        ({ schemaCoordinate, id }) =>
          schemaCoordinate === selectedCoordinate &&
          (!selectedCommentId || selectedCommentId === id),
      )?.id,
    [parentComments, selectedCommentId, selectedCoordinate],
  );

  const hasBuildErrors = isMinLengthArray(1, buildErrors);
  const selectedApiOrSupergraphTab =
    selectedSchemaInfo.id === API_SCHEMA_ID ||
    selectedSchemaInfo.id === SUPERGRAPH_SCHEMA_ID;
  const hasBanners =
    hasBuildErrors || (selectedApiOrSupergraphTab && dataIsNotConsistent);

  return (
    <>
      <div className={className}>
        <div className={classnames(hasBanners && 'pl-2 pr-6 pt-6')}>
          {hasBuildErrors &&
            (selectedApiOrSupergraphTab ? (
              <Alert status="warning">
                This variant of the{' '}
                {selectedSchemaInfo.id === API_SCHEMA_ID ? 'API' : 'Supergraph'}{' '}
                schema does not include the latest from some of your subgraphs
                because the supergraph schema build failed.{' '}
                <Link
                  className="inline-block font-semibold underline"
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.buildErrors,
                  })}
                >
                  See details
                </Link>
              </Alert>
            ) : (
              <Alert status="warning">
                Supergraph schema build failed.{' '}
                <Link
                  className="inline-block font-semibold underline"
                  to={setOverlayVisibleLocation({
                    location,
                    overlayName: Config.modals.buildErrors,
                  })}
                >
                  See details
                </Link>
              </Alert>
            ))}
          <PermissionGuard
            graphId={graphRef.graphId}
            graphVariant={graphRef.graphVariant}
            graphVariantPermissions="canQueryLaunches"
            fallback={null}
          >
            {!hasBuildErrors &&
              selectedApiOrSupergraphTab &&
              dataIsNotConsistent && (
                <Alert status="warning">
                  Currently composing your graph! Your recent changes will be
                  reflected here shortly.
                </Alert>
              )}
          </PermissionGuard>
        </div>
        <div className="relative flex size-full overflow-hidden">
          <div
            className={classnames(
              'mb-6 mr-6 flex min-h-0 w-0 flex-1 flex-col overflow-hidden rounded-lg border border-primary bg-primary',
              !collapsed && 'ml-2',
              hasBanners ? 'mt-3' : 'mt-6',
            )}
          >
            <ProposalEditorActions
              selectedSchemaInfo={selectedSchemaInfo}
              editor={editor}
              graphRef={graphRef}
              setKeyboardModalVisible={setKeyboardModalVisible}
              setCurrentVisibleDrawer={setCurrentVisibleDrawer}
              sdlIsLoading={sdlIsLoading}
            />
            {sdlIsLoading ? (
              <SDLShimmerLoading />
            ) : (
              <ProposalSDLEditor
                editor={editor}
                selectedSchemaInfo={selectedSchemaInfo}
                parentComments={parentComments}
                selectedSubgraphHasChanges={selectedSubgraphHasChanges}
              />
            )}
          </div>
          {selectedSchemaInfo.type === 'Subgraph' &&
            shouldShowLinter &&
            currentVisibleDrawer === 'linter' && (
              <SchemaLinterDrawer
                onClose={() => setCurrentVisibleDrawer('none')}
                className="max-md:absolute max-md:right-0 max-md:top-0"
                selectedSchemaInfo={selectedSchemaInfo}
                editor={editor}
                changedSubgraphs={changedSubgraphs}
                graphId={graphRef.graphId}
                rules={linterRules}
                proposedSubgraphs={proposedSubgraphs}
                proposedSupergraphSchema={proposedSupergraphSchema}
              />
            )}
          {currentVisibleDrawer === 'unattributedDiagnostics' && (
            <UnattributedDiagnosticsDrawer
              onClose={() => setCurrentVisibleDrawer('none')}
              className="max-md:absolute max-md:right-0 max-md:top-0"
            />
          )}
          <ProposalCommentDrawer
            implementedChanges={implementedChanges}
            canQueryLaunches={canQueryLaunches}
            sourceGraphVariant={sourceGraphVariant}
            isOpen={currentVisibleDrawer === 'comment'}
            onClose={onCloseCommentDrawer}
            className="max-md:absolute max-md:right-0 max-md:top-0"
            commentThreadId={commentThreadToOpen}
            graphRef={graphRef}
            proposalId={proposalId}
            schemaScope={selectedSchemaInfo.id}
            parentComments={parentComments}
            isStale={isStale}
          />
        </div>
      </div>
      {keyboardModalVisible && (
        <KeyboardShortcutModal
          onClose={() => setKeyboardModalVisible(false)}
          shortcuts={shortcuts}
        />
      )}
      <LazyUrlModal name={Config.modals.deleteProposalComment}>
        {({ closeModal, isOpen }) => (
          <DeleteCommentModal
            proposalId={proposalId}
            graphRef={graphRef}
            isOpen={isOpen}
            onClose={() =>
              closeModal(undefined, ['proposalCommentId', 'commentThreadId'])
            }
          />
        )}
      </LazyUrlModal>
    </>
  );
};
